import { defaultGroupAdminCapabilityAdds, defaultGroupOwnerCapabilityAdds, defaultGroupRoles, } from '../types/capabilities.js';
import { checkCapabilities, getGroupCapabilitiesByRoles, } from './capabilitiesHelpers.js';
import { profileName } from './profileHelpers.js';
const withModerator = (member) => {
    var _a, _b;
    return ({
        add: [...((_a = member.add) !== null && _a !== void 0 ? _a : []), 'core-posts-delete'],
        ...(((_b = member.remove) === null || _b === void 0 ? void 0 : _b.length) ? { remove: member.remove } : {}),
    });
};
const ownerCapabilities = {
    add: [...defaultGroupOwnerCapabilityAdds],
};
const adminCapabilities = {
    add: [...defaultGroupAdminCapabilityAdds],
};
const containsIgnoreCase = (candidate, query) => candidate.toLowerCase().indexOf(query.toLowerCase()) > -1;
export const groupName = (group) => (group === null || group === void 0 ? void 0 : group.displayName) || (group === null || group === void 0 ? void 0 : group.handle) || '';
export const matchesGroupQuery = (group, query, queryOptions = { handle: true, displayName: true, description: false }) => (queryOptions.handle && containsIgnoreCase(group.handle, query)) ||
    (queryOptions.displayName &&
        group.displayName &&
        containsIgnoreCase(group.displayName, query)) ||
    (queryOptions.description &&
        group.description &&
        containsIgnoreCase(group.description, query));
export const sortGroupMembers = (members) => {
    var _a;
    return (_a = members === null || members === void 0 ? void 0 : members.sort((a, b) => profileName(a.profile).localeCompare(profileName(b.profile)))) !== null && _a !== void 0 ? _a : [];
};
export const groupMembershipHasRole = (roles, role) => (roles !== null && roles !== void 0 ? roles : []).includes(role);
export const actorIsGroupOwner = (profile, groupId) => {
    var _a, _b;
    return groupMembershipHasRole((_b = (_a = profile.memberships) === null || _a === void 0 ? void 0 : _a.find((m) => m.group.id === groupId)) === null || _b === void 0 ? void 0 : _b.roles, 'owner');
};
export const countGroupOwners = (members) => members.filter((m) => groupMembershipHasRole(m.roles, 'owner')).length;
export const groupRoleChangeBlocked = ({ actorIsOwner, currentRoles, nextRoles, ownerCount, }) => {
    const wasOwner = groupMembershipHasRole(currentRoles, 'owner');
    const willBeOwner = groupMembershipHasRole(nextRoles, 'owner');
    if (wasOwner !== willBeOwner && !actorIsOwner) {
        return 'owner-assignment';
    }
    if (wasOwner && !willBeOwner && ownerCount <= 1) {
        return 'last-owner';
    }
    return undefined;
};
export const groupOwnerRemovalBlocked = ({ targetRoles, ownerCount, }) => groupMembershipHasRole(targetRoles, 'owner') && ownerCount <= 1;
export const assignableGroupRoles = (actorIsOwner) => actorIsOwner
    ? [...defaultGroupRoles]
    : defaultGroupRoles.filter((role) => role !== 'owner');
export const groupRolesForAssignment = (role) => (role === 'member' ? ['member'] : ['member', role]);
export const canChangeMemberRole = (profile, group) => {
    var _a, _b, _c;
    return checkCapabilities(['core-groups-changeMemberRole'], getGroupCapabilitiesByRoles((_c = (_b = (_a = profile.memberships) === null || _a === void 0 ? void 0 : _a.find((m) => m.group.id === group.id)) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [], group)).success;
};
export const canAddMember = (profile, group) => {
    var _a, _b, _c;
    return checkCapabilities(['core-groups-addMember'], getGroupCapabilitiesByRoles((_c = (_b = (_a = profile.memberships) === null || _a === void 0 ? void 0 : _a.find((m) => m.group.id === group.id)) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [], group)).success;
};
export const canRemoveMember = (profile, group) => {
    var _a, _b, _c;
    return checkCapabilities(['core-groups-removeMember'], getGroupCapabilitiesByRoles((_c = (_b = (_a = profile.memberships) === null || _a === void 0 ? void 0 : _a.find((m) => m.group.id === group.id)) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [], group)).success;
};
export const isGroupDiscoverable = (group) => checkCapabilities(['core-groups-read'], getGroupCapabilitiesByRoles(['none'], group)).success;
/** Posts are only readable by explicit group members (not public/local). */
export const hasMemberOnlyPostsVisibility = (capabilities) => {
    var _a, _b, _c, _d;
    return !((_b = (_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.none) === null || _a === void 0 ? void 0 : _a.add) === null || _b === void 0 ? void 0 : _b.includes('core-posts-read')) &&
        !((_d = (_c = capabilities === null || capabilities === void 0 ? void 0 : capabilities.local) === null || _c === void 0 ? void 0 : _c.add) === null || _d === void 0 ? void 0 : _d.includes('core-posts-read'));
};
export const groupCapabilityTemplates = {
    defaultGroup: {
        name: 'defaultGroup',
        capabilities: {
            none: {
                add: ['core-groups-read', 'core-posts-read'],
            },
            local: {
                add: [
                    'core-groups-join',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            member: {
                add: [
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            },
            moderator: withModerator({
                add: [
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    defaultGroupClosedCommunity: {
        name: 'defaultGroupClosedCommunity',
        capabilities: {
            local: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-groups-join',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
                remove: ['core-posts-create-event'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    publicGroup: {
        name: 'publicGroup',
        capabilities: {
            none: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-react',
                ],
            },
            member: {
                add: [
                    'core-posts-create-*',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-posts-create-*',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    localGroup: {
        name: 'localGroup',
        capabilities: {
            local: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-react',
                ],
            },
            member: {
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-groups-join',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    privateGroup: {
        name: 'privateGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-create-*',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    limitedPostingGroup: {
        name: 'limitedPostingGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    announcementGroup: {
        name: 'announcementGroup',
        capabilities: {
            member: {
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            },
            moderator: withModerator({
                add: [
                    'core-groups-read',
                    'core-posts-read',
                    'core-posts-react',
                    'core-posts-reply',
                    'core-posts-rsvp',
                    'core-posts-vote',
                ],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
    lockedGroup: {
        name: 'lockedGroup',
        capabilities: {
            none: {
                add: ['core-groups-read', 'core-posts-read', 'core-posts-react'],
            },
            member: {
                add: ['core-posts-reply', 'core-posts-rsvp', 'core-posts-vote'],
            },
            moderator: withModerator({
                add: ['core-posts-reply', 'core-posts-rsvp', 'core-posts-vote'],
            }),
            admin: adminCapabilities,
            owner: ownerCapabilities,
        },
    },
};
